import { Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import Debug from 'debug';
import store from '@/store';
import { injectable } from 'inversify';
import { createUpserter, mergeRightToLeft } from '@/app/shared/utils'
import { defaultBadgeState, BadgeState } from './badge-state'
import { Badge } from './badge'
import { BadgeStatus } from './badge-status';
import { BadgesQuery, LoadingPhase, RemoteStateQueryPhase } from './badge-query';

export const BadgeStoreModuleSymbol = Symbol.for('BadgeStoreModule');

const upserter = createUpserter(defaultBadgeState);

const debug = Debug('badge-store-module');

@injectable()
@Module({
  namespaced: true,
  name: 'badge',
  store,
  dynamic: true
})
export class BadgeStoreModule extends VuexModule implements BadgesQuery {

  private remoteStateQueryPhase: RemoteStateQueryPhase = 'none';
  private descriptorLoadingPhase: LoadingPhase = 'none';
  private loadedBadges: Badge[] = [];

  private localState: BadgeState[] = [];
  private remoteState: BadgeState[] = [];

  @Mutation
  public startLoading() {
    this.descriptorLoadingPhase = 'loading';
    this.loadedBadges = [];
  }

  @Mutation
  public finishLoading(badges: Badge[]) {
    this.loadedBadges = badges;
    this.descriptorLoadingPhase = 'loaded';
  }

  get isLoading() {
    return this.descriptorLoadingPhase === 'loading';
  }

  @Mutation
  public startRemoteQuerying() {
    this.remoteStateQueryPhase = 'loading';
    this.remoteState = [];
  }

  @Mutation
  public finishRemoteQuerying(badges: BadgeState[]) {
    this.remoteStateQueryPhase = 'loaded';
    this.remoteState = badges;
  }

  @Mutation
  public setAsEarned({ids, date}: { ids: string[], date: Date }) {

    for (const id of ids) {
      this.localState = upserter(this.localState, id, (current) => {
        return {
          id: current.id,
          status: 'earned',
          date
        };
      });
    }
  }

  @Mutation
  public setAsNotEarned(ids: string[]) {
    for (const id of ids) {
      this.localState = upserter(this.localState, id, (current) => {
        return {
          id: current.id,
          status: 'not-earned'
        };
      });
    }
  }

  get loadingPhase() {
    return this.descriptorLoadingPhase;
  }

  get remotePhase() {
    return this.remoteStateQueryPhase;
  }

  get isRemoteLoading() {
    return this.remoteStateQueryPhase === 'loading';
  }

  get isRemoteLoaded() {
    return this.remoteStateQueryPhase === 'loaded';
  }

  get badgeDescriptors() {
    return this.loadedBadges;
  }

  get badges() {

    const merged = mergeRightToLeft(this.localState, this.remoteState, merge)
    debug('merged', this.localState, this.remoteState, merged);

    return this.loadedBadges.map((badge) => {

      const found = merged.find((x) => x.id === badge.id);
      const status = found ? found.status : 'unknown';
      const date = found && found.status === 'earned' ? found.date : null;

      return {
        id: badge.id,
        title: badge.title,
        imageUri: badge.imageUri,
        descriptionFragment: badge.descriptionFragment,
        earnedDescriptionFragment: badge.earnedDescriptionFragment,
        status,
        date
      };
    });
  }

  get isLocallyEarned() {

    const self = this;
    return function(id: string) {

      return isEarned(id, self.localState);
    };
  }

  get isRemotelyEarned() {
    const self = this;
    return function(id: string) {

      return isEarned(id, self.remoteState);
    };
  }

  get locallyEarnedBadges() {
    return this.localState.filter((x) => x.status === 'earned').map((x) => {

      if (x.status !== 'earned') { throw new Error(); }

      return {
        id: x.id,
        date: x.date
      };
    });
  }
}

function merge(local: BadgeState, remote: BadgeState): BadgeState {

  const status = statusAsNumber(remote.status) >= statusAsNumber(local.status) ?
        remote.status : local.status

  if (status === 'earned') {

    let date
    if (remote.status === 'earned') {
      date = remote.date
    } else if (local.status === 'earned') {
      date = local.date
    } else {
      throw new Error('Coding error: one of the date should be set')
    }
    
    return {
      id: local.id,
      status,
      date: date
    }
  }

  return {
    id: local.id,
    status
  }
}

function statusAsNumber(status: BadgeStatus) {
  switch (status) {
    case 'unknown':
      return 0;
    case 'not-earned':
      return 1;
    case 'earned':
      return 2;
  }
}

function isEarned(id: string, states: BadgeState[]) {
  const status = badgeStatus(id, states);
  return status === 'earned';
}

function badgeStatus(id: string, states: BadgeState[]) {
  const current = states.find((x) => x.id === id);
  if (!current) { return 'unknown'; }

  return current.status;
}


export default getModule(BadgeStoreModule);
